// ─── src/components/dashboard/Sidebar.tsx ────────────────────────────────────
//
// Grouped left sidebar for the master dashboard, replacing the previous
// horizontal tab strip. Scales to many views (25+ items possible) without
// the tabs falling off the edge of the screen.
//
// Groups are declared in the parent's `NAV_GROUPS` config; this component
// only cares about rendering + highlighting the active item and toggling
// mobile visibility.

import { Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import { X } from "lucide-react";

export interface SidebarItem<K extends string = string> {
  key: K;
  label: string;
  /** Optional small icon on the leading edge of the row. */
  icon?: LucideIcon;
}

export interface SidebarGroup<K extends string = string> {
  /** Section header text. */
  label: string;
  /** Emoji or lucide icon inline with the header. */
  emoji?: string;
  items: SidebarItem<K>[];
}

interface Props<K extends string> {
  groups: SidebarGroup<K>[];
  active: K;
  onSelect: (key: K) => void;
  /** Mobile-only: whether the drawer is open. Desktop ignores this. */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar<K extends string>({
  groups, active, onSelect, mobileOpen, onMobileClose,
}: Props<K>) {
  const handleSelect = (k: K) => {
    onSelect(k);
    onMobileClose();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={onMobileClose}
          aria-hidden
        />
      )}

      <aside
        className={`
          bg-brand-card border-r border-brand-border
          flex flex-col
          transition-transform duration-200
          lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:w-64 lg:shrink-0
          fixed inset-y-0 left-0 z-50 w-72
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        aria-label="Dashboard navigation"
      >
        {/* Mobile close button */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-brand-border">
          <span className="text-white font-semibold">Navigation</span>
          <button
            onClick={onMobileClose}
            className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-5">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                {group.emoji && <span className="mr-1.5">{group.emoji}</span>}
                {group.label}
              </p>
              <ul className="mt-1.5 space-y-0.5">
                {group.items.map((item) => {
                  const isActive = item.key === active;
                  const Icon = item.icon;
                  return (
                    <li key={item.key}>
                      <button
                        onClick={() => handleSelect(item.key)}
                        className={`
                          w-full flex items-center gap-2 px-3 py-2 rounded-lg
                          text-sm font-medium transition-colors
                          ${isActive
                            ? "bg-brand-green/15 text-brand-green"
                            : "text-brand-muted hover:text-white hover:bg-brand-border/40"
                          }
                        `}
                      >
                        {Icon && <Icon size={15} className="shrink-0" />}
                        <span className="truncate text-left flex-1">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-brand-border">
          <p className="text-[10px] uppercase tracking-widest text-brand-muted px-3 py-1">
            SW Syria Dashboard
          </p>
        </div>
      </aside>

      {/* Consume the discriminated-item generic — silences unused warnings on
          items that only render conditionally. */}
      <Fragment />
    </>
  );
}
